/**
 * Device-local "last used" play settings (localStorage).
 *
 * Same pattern the dashboard hero already used for variant/cadence (commit e59a957b):
 * these are per-device "last used" values, deliberately kept OUT of the synced
 * preferences blob (usePreferencesStore). This is the single source of truth for the
 * three play surfaces — Gioca (PlayOverlay, and the option rows it shares with the
 * direct challenge), Sfida AI (DashboardPlayAIModal) and Sfida diretta
 * (the custom-game sheet): each one seeds its dropdowns from here and writes back on
 * every change, so the next open restores the last choice.
 *
 * The AI and direct-challenge modals opt in via `rememberSettings`; the editor and
 * puzzle consumers do not, so they are unaffected. The lobby's variant picker always
 * remembers — it is the only variant choice left in the online play flow.
 *
 * Non sono tutte cadenze e varianti: in fondo ci sono anche i due interruttori
 * audio dell'orologio dal vivo. Stanno qui per il criterio del file — roba per
 * DISPOSITIVO, deliberatamente fuori dalle preferenze sincronizzate — non perche'
 * siano un'impostazione di gioco.
 */
import {
  VALID_VARIANT_STRINGS,
  CHALLENGE_COLORS,
  isMatchType,
  GAME_MODES,
  normalizeOpeningMode,
  TIME_CONTROL_PRESETS,
  parseTimeControlKey,
} from '@draughts/shared';
import type { ChallengeColor, GameMode, MatchLegs, MatchType, OpeningMode, TimeControlKey } from '@draughts/shared';
// Type-only import (erased at runtime) so this util never forms a runtime import cycle
// with the AI modal, which imports the setters below.
import type { AIDifficulty } from '../components/dashboard/DashboardPlayAIModal';

// --- Keys --------------------------------------------------------------------
// Variant + shared-cadence keys are reused VERBATIM from DashboardPage so the hero
// and the modals share one memory (and the existing DashboardPage tests keep passing).
export const LAST_VARIANT_KEY = 'draughts-dashboard-variant';
export const LAST_TIMECONTROL_KEY = 'draughts-dashboard-timecontrol';
// The hero rating toggle gets its OWN cadence key, deliberately separate from the shared
// modal cadence above: switching which rating the hero shows must NOT clobber the cadence
// the play modals remember. Same value space as LAST_TIMECONTROL_KEY (presets, never 'none').
export const LAST_RATING_TC_KEY = 'draughts-dashboard-rating-timecontrol';
// Sfida diretta gets its OWN cadence key so it can remember '∞' (none) without ever
// feeding 'none' to the dashboard summary (which 500s on it — the reason e59a957b guards it).
export const LAST_CHALLENGE_TIMECONTROL_KEY = 'draughts-last-challenge-timecontrol';
export const LAST_AI_DIFFICULTY_KEY = 'draughts-last-ai-difficulty';
// Sfida AI: apertura / colore / modalità hanno chiavi PROPRIE, separate da quelle della
// Sfida diretta più sotto. Provare l'antidama contro il computer non deve cambiare in
// silenzio le impostazioni della prossima sfida a un umano (che è per giunta valutata).
export const LAST_AI_OPENING_KEY = 'draughts-last-ai-opening';
export const LAST_AI_COLOR_KEY = 'draughts-last-ai-color';
export const LAST_AI_MODE_KEY = 'draughts-last-ai-mode';
export const LAST_CHALLENGE_COLOR_KEY = 'draughts-last-challenge-color';
export const LAST_CHALLENGE_MATCH_TYPE_KEY = 'draughts-last-challenge-match-type';
export const LAST_CHALLENGE_MODE_KEY = 'draughts-last-challenge-mode';
export const LAST_CHALLENGE_OPENING_KEY = 'draughts-last-challenge-opening';
export const LAST_CHALLENGE_LEGS_KEY = 'draughts-last-challenge-legs';
// L'orologio dal vivo ha DUE chiavi, una per lato: l'attrezzo offre tempi
// asimmetrici (l'handicap), e una chiave sola non potrebbe ricordarli. Accettano
// qualunque cadenza della griglia ma NON '∞': un orologio senza tempo non e' un
// orologio, e la sua lista di minuti non offre l'infinito.
export const LAST_OROLOGIO_BIANCO_KEY = 'draughts-last-orologio-bianco';
export const LAST_OROLOGIO_NERO_KEY = 'draughts-last-orologio-nero';
// Il criterio dell'ultimo sorteggio di aperture (un `OpeningMode`). La VARIANTE
// no: quella riusa LAST_VARIANT_KEY, cioe' la stessa memoria delle altre
// superfici di gioco — chi gioca di italiana vuole le aperture italiane.
// La chiave non e' quella della Sfida diretta: estrarre un'apertura per una
// partita al tavolo non deve cambiare in silenzio il predefinito di una sfida
// online, che e' per giunta valutata.
export const LAST_APERTURA_MODO_KEY = 'draughts-last-apertura-modo';

// --- Raw storage (safe under private mode / storage disabled) -----------------
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore (private mode / storage disabled) */
  }
}

// Valid preset strings (e.g. 'blitz-5+3'); 'none' (∞) is intentionally NOT among them.
const TIME_CONTROL_PRESET_VALUES = Object.keys(TIME_CONTROL_PRESETS);
// Mirror of DIFFICULTIES in DashboardPlayAIModal.tsx (kept here to avoid a runtime cycle).
const AI_DIFFICULTIES: readonly AIDifficulty[] = ['very-easy', 'easy', 'medium', 'hard', 'expert', 'master'];

// --- Variant (lowercase-canonical) -------------------------------------------
export function getLastVariant(): string | null {
  const v = read(LAST_VARIANT_KEY);
  return v && VALID_VARIANT_STRINGS.includes(v) ? v : null;
}
export function setLastVariant(variant: string): void {
  write(LAST_VARIANT_KEY, variant.toLowerCase());
}

// --- Shared cadence (hero + Gioca subito) — never 'none' ----------------------
export function getLastTimeControl(): string | null {
  const tc = read(LAST_TIMECONTROL_KEY);
  return tc && TIME_CONTROL_PRESET_VALUES.includes(tc) ? tc : null;
}
export function setLastTimeControl(tc: string): void {
  // 'none' (∞) must never reach the dashboard summary — the shared key omits it.
  if (tc && tc !== 'none' && TIME_CONTROL_PRESET_VALUES.includes(tc)) {
    write(LAST_TIMECONTROL_KEY, tc);
  }
}

// --- Hero rating cadence — dedicated key, never 'none' ------------------------
// Sticky "which rating is shown" selection, independent of the modal cadence above.
export function getLastRatingTimeControl(): string | null {
  const tc = read(LAST_RATING_TC_KEY);
  return tc && TIME_CONTROL_PRESET_VALUES.includes(tc) ? tc : null;
}
export function setLastRatingTimeControl(tc: string): void {
  // Guard 'none' identically: it isn't a rating category and would 500 the summary.
  if (tc && tc !== 'none' && TIME_CONTROL_PRESET_VALUES.includes(tc)) {
    write(LAST_RATING_TC_KEY, tc);
  }
}

// --- Sfida diretta cadence — dedicated key, accepts '∞' and custom cadences ---
// Validated by PARSING, not by membership: a direct challenge can be played at
// any minutes×increment, so the preset list above would silently drop e.g.
// 'rapid-5+7' and reset the modal to the default on every reopen.
function isChallengeTimeControl(tc: string): boolean {
  return tc === 'none' || parseTimeControlKey(tc) !== null;
}
export function getLastChallengeTimeControl(): string | null {
  const tc = read(LAST_CHALLENGE_TIMECONTROL_KEY);
  return tc && isChallengeTimeControl(tc) ? tc : null;
}
export function setLastChallengeTimeControl(tc: string): void {
  if (tc && isChallengeTimeControl(tc)) {
    write(LAST_CHALLENGE_TIMECONTROL_KEY, tc);
  }
}

// --- AI difficulty ------------------------------------------------------------
export function getLastAiDifficulty(): AIDifficulty | null {
  const d = read(LAST_AI_DIFFICULTY_KEY);
  return d && AI_DIFFICULTIES.includes(d as AIDifficulty) ? (d as AIDifficulty) : null;
}
export function setLastAiDifficulty(d: AIDifficulty): void {
  write(LAST_AI_DIFFICULTY_KEY, d);
}

// --- Sfida AI: apertura / colore / modalità -----------------------------------
export function getLastAiOpening(): OpeningMode | null {
  const o = read(LAST_AI_OPENING_KEY);
  // Come per la sfida diretta: le grafie storiche vengono rimappate invece di
  // riportare la preferenza a "mossa libera".
  return o ? normalizeOpeningMode(o) : null;
}
export function setLastAiOpening(o: OpeningMode): void {
  write(LAST_AI_OPENING_KEY, o);
}

export function getLastAiColor(): ChallengeColor | null {
  const c = read(LAST_AI_COLOR_KEY);
  return c && CHALLENGE_COLORS.includes(c as ChallengeColor) ? (c as ChallengeColor) : null;
}
export function setLastAiColor(c: ChallengeColor): void {
  write(LAST_AI_COLOR_KEY, c);
}

export function getLastAiGameMode(): GameMode | null {
  const m = read(LAST_AI_MODE_KEY);
  return m && GAME_MODES.includes(m as GameMode) ? (m as GameMode) : null;
}
export function setLastAiGameMode(m: GameMode): void {
  write(LAST_AI_MODE_KEY, m);
}

// --- Sfida diretta: color / rated / mode / opening ----------------------------
export function getLastChallengeColor(): ChallengeColor | null {
  const c = read(LAST_CHALLENGE_COLOR_KEY);
  return c && CHALLENGE_COLORS.includes(c as ChallengeColor) ? (c as ChallengeColor) : null;
}
export function setLastChallengeColor(c: ChallengeColor): void {
  write(LAST_CHALLENGE_COLOR_KEY, c);
}

/**
 * The game type last chosen. New key rather than a migration of the old
 * `draughts-last-challenge-rated` boolean: that value could not tell "solo
 * valida" from "qualsiasi" — both were `true` — so there is nothing to carry
 * over, and the preference simply starts again from the default.
 */
export function getLastChallengeMatchType(): MatchType | null {
  const v = read(LAST_CHALLENGE_MATCH_TYPE_KEY);
  return isMatchType(v) ? v : null;
}
export function setLastChallengeMatchType(matchType: MatchType): void {
  write(LAST_CHALLENGE_MATCH_TYPE_KEY, matchType);
}

export function getLastChallengeGameMode(): GameMode | null {
  const m = read(LAST_CHALLENGE_MODE_KEY);
  return m && GAME_MODES.includes(m as GameMode) ? (m as GameMode) : null;
}
export function setLastChallengeGameMode(m: GameMode): void {
  write(LAST_CHALLENGE_MODE_KEY, m);
}

export function getLastChallengeOpening(): OpeningMode | null {
  const o = read(LAST_CHALLENGE_OPENING_KEY);
  if (!o) return null;
  // Devices still hold the pre-ballot spellings (`drawn`, `drawn2`, `drawn3`);
  // normalizeOpeningMode maps each onto the mode that draws the same pool it used
  // to, instead of silently resetting the preference to "free".
  return normalizeOpeningMode(o);
}
export function setLastChallengeOpening(o: OpeningMode): void {
  write(LAST_CHALLENGE_OPENING_KEY, o);
}

export function getLastChallengeLegs(): MatchLegs | null {
  const l = read(LAST_CHALLENGE_LEGS_KEY);
  if (l === '1') return 1;
  if (l === '2') return 2;
  return null;
}
export function setLastChallengeLegs(legs: MatchLegs): void {
  write(LAST_CHALLENGE_LEGS_KEY, String(legs));
}

// --- Orologio dal vivo: una cadenza per lato ---------------------------------
// Validate per PARSING come la Sfida diretta, e per la stessa ragione (la griglia
// vale quasi cinquecento combinazioni), ma senza il ramo 'none'.
function isCadenzaOrologio(tc: string): boolean {
  return parseTimeControlKey(tc) !== null;
}
function leggiCadenza(chiave: string): TimeControlKey | null {
  const tc = read(chiave);
  return tc && isCadenzaOrologio(tc) ? (tc as TimeControlKey) : null;
}
export function getLastOrologioBianco(): TimeControlKey | null {
  return leggiCadenza(LAST_OROLOGIO_BIANCO_KEY);
}
export function getLastOrologioNero(): TimeControlKey | null {
  return leggiCadenza(LAST_OROLOGIO_NERO_KEY);
}
export function setLastOrologioBianco(tc: string): void {
  if (isCadenzaOrologio(tc)) write(LAST_OROLOGIO_BIANCO_KEY, tc);
}
export function setLastOrologioNero(tc: string): void {
  if (isCadenzaOrologio(tc)) write(LAST_OROLOGIO_NERO_KEY, tc);
}

// --- Orologio dal vivo: i suoi due suoni -------------------------------------
/*
 * I due interruttori audio dell'ATTREZZO, e non quelli del profilo.
 *
 * Decisione esplicita: l'orologio da tavolo ha i suoi, separati da
 * `moveSoundEnabled`/`lowTimeSoundEnabled` di `usePreferencesStore`. Chi gioca
 * online col telefono muto non per questo vuole un orologio muto sul tavolo, e
 * viceversa. Stanno quindi qui, per dispositivo, accanto alle due cadenze
 * dell'attrezzo e fuori dal blob sincronizzato — che e' il criterio di tutto
 * questo file.
 *
 * Li legge `utils/audio.ts` (la mappa `CANALE_ACCESO`): il cancello dei suoni
 * resta uno solo, e chi suona non controlla niente da se'.
 */
export const SUONO_TOCCO_OROLOGIO_KEY = 'draughts-orologio-suono-tocco';
export const SUONO_TEMPO_BASSO_OROLOGIO_KEY = 'draughts-orologio-suono-poco-tempo';

/*
 * ACCESO e' il default, e per questo si confronta con '0' invece di con '1': un
 * valore assente — primo avvio, modalita' privata, storage negato — deve
 * suonare, non tacere. Un orologio da tavolo che non si sente non serve a
 * niente, e nessuno andrebbe a cercare l'interruttore per accenderlo.
 */
function leggiAcceso(chiave: string): boolean {
  return read(chiave) !== '0';
}
function scriviAcceso(chiave: string, acceso: boolean): void {
  write(chiave, acceso ? '1' : '0');
}
export function getSuonoToccoOrologio(): boolean {
  return leggiAcceso(SUONO_TOCCO_OROLOGIO_KEY);
}
export function setSuonoToccoOrologio(acceso: boolean): void {
  scriviAcceso(SUONO_TOCCO_OROLOGIO_KEY, acceso);
}
export function getSuonoTempoBassoOrologio(): boolean {
  return leggiAcceso(SUONO_TEMPO_BASSO_OROLOGIO_KEY);
}
export function setSuonoTempoBassoOrologio(acceso: boolean): void {
  scriviAcceso(SUONO_TEMPO_BASSO_OROLOGIO_KEY, acceso);
}

// --- Estrai apertura: il criterio del sorteggio -------------------------------
// Il valore e' un `OpeningMode`, lo stesso vocabolario del menu «Apertura» della
// partita personalizzata — meno 'free' e 'single', che l'attrezzo non offre:
// quella schermata ESTRAE, e una mossa libera non si estrae.
//
// Non si convalida contro la variante: `resolveOpeningMode` lo fa a schermo a
// ogni render (un ballot ricordato diventa tre semi-mosse a caso dove la tabella
// federale non c'e'), e qui una variante non c'e' nemmeno da guardare.
function isModoEstraibile(o: string): boolean {
  /*
   * `normalizeOpeningMode` torna 'free' per tutto cio' che non riconosce —
   * compresa la vecchia TABELLA rimasta sui dispositivi ('A'..'D', 'generale')
   * e 'single', che non e' fra `OPENING_MODES`. Quindi il confronto con
   * l'originale e' insieme la convalida e il rifiuto del solo valore che questo
   * attrezzo non puo' usare: chi non convalida torna al predefinito.
   */
  return o !== 'free' && normalizeOpeningMode(o) === o;
}
export function getLastAperturaModo(): OpeningMode | null {
  const o = read(LAST_APERTURA_MODO_KEY);
  return o && isModoEstraibile(o) ? (o as OpeningMode) : null;
}
export function setLastAperturaModo(o: OpeningMode): void {
  if (isModoEstraibile(o)) write(LAST_APERTURA_MODO_KEY, o);
}
